import type { UnitKind } from './enums';

/**
 * Areas (docs/adr/0010-flaechen-keller-aussenflaechen.md).
 *
 * `unit.area` is the engine input for the area key. For flats and commercial units it is the living area
 * (Wohnfläche nach WoFlV) or the usable area of the shop; for cellars, storage rooms and parking spaces it is only an
 * informative usable area (Nutzfläche) and never part of the living-area key.
 */

/** Unit kinds whose `area` is part of the area key (Wohnfläche / Gewerbefläche). */
export const AREA_KEY_UNIT_KINDS = ['apartment', 'commercial'] as const satisfies readonly UnitKind[];
export const countsForAreaKey = (kind: string) => (AREA_KEY_UNIT_KINDS as readonly string[]).includes(kind);

/** Which label the area field of a unit gets: Wohnfläche, Nutzfläche (Gewerbe) or Nutzfläche (optional). */
export type AreaFieldKind = 'living' | 'commercial' | 'usable';
export const areaFieldKind = (kind: string): AreaFieldKind => (kind === 'apartment' ? 'living' : kind === 'commercial' ? 'commercial' : 'usable');
/** Flats and commercial units need an area; for cellars, storage and parking it is optional. */
export const areaRequired = (kind: string) => countsForAreaKey(kind);

/**
 * Room types of the WoFlV area calculator (§ 4 WoFlV):
 * - `full`: clear height ≥ 2 m → 100 %
 * - `slope`: clear height 1 m to < 2 m (Dachschräge) → 50 %
 * - `low`: clear height < 1 m → 0 %
 * - `outdoor_quarter`: balcony, loggia, roof garden, terrace (rule) → 25 %
 * - `outdoor_half`: same, high quality / exceptional → at most 50 %
 * - `winter_garden`: unheated winter garden or similar room closed on all sides → 50 %
 */
export const AREA_ROOM_TYPES = ['full', 'slope', 'low', 'outdoor_quarter', 'outdoor_half', 'winter_garden'] as const;
export type AreaRoomType = (typeof AREA_ROOM_TYPES)[number];

/** Counting factor per type in quarters (4 = 100 %), so all sums stay integers. */
export const AREA_ROOM_QUARTERS: Record<AreaRoomType, 0 | 1 | 2 | 4> = { full: 4, slope: 2, low: 0, outdoor_quarter: 1, outdoor_half: 2, winter_garden: 2 };

/**
 * Parses a user-entered area ("12,5", "12.50", "7") into centi-m² (hundredths of a square metre) without floats.
 * At most two decimals; anything else (negative, letters, 3 decimals, thousands separators) → null.
 */
export function parseCentiM2(input: string): number | null {
  const s = input.trim();
  const m = /^(\d{1,6})(?:[.,](\d{1,2}))?$/.exec(s);
  if (!m) return null;
  return Number(m[1]) * 100 + Number((m[2] ?? '').padEnd(2, '0'));
}

export type AreaRoom = { centiM2: number; type: AreaRoomType };
export type AreaCalcResult = {
  /** Counted area per room in quarter-centi-m² (exact). */
  rows: { quarterCenti: number }[];
  /** Exact counted sum in quarter-centi-m². */
  exactQuarterCenti: number;
  /** Counted area in centi-m², rounded half up at the very end (only the sum is rounded). */
  totalCentiM2: number;
  /** Sum of the floor areas as entered (100 % each), centi-m². */
  grossCentiM2: number;
};

/** WoFlV living area from room rows: Σ floor area × factor, exact in quarters, rounded once to 0,01 m². */
export function woflvArea(rooms: AreaRoom[]): AreaCalcResult {
  let exact = 0;
  let gross = 0;
  const rows = rooms.map((r) => {
    if (!Number.isInteger(r.centiM2) || r.centiM2 < 0) throw new Error('centiM2 must be a non-negative integer');
    const q = r.centiM2 * AREA_ROOM_QUARTERS[r.type];
    exact += q;
    gross += r.centiM2;
    return { quarterCenti: q };
  });
  return { rows, exactQuarterCenti: exact, totalCentiM2: Math.floor((exact + 2) / 4), grossCentiM2: gross };
}

/** centi-m² → plain decimal string with the given separator, no grouping ("1234,5" style, trailing zeros dropped). */
export function centiM2ToInput(centi: number, separator = ','): string {
  const whole = Math.floor(centi / 100);
  const frac = centi % 100;
  if (frac === 0) return String(whole);
  return `${whole}${separator}${String(frac).padStart(2, '0').replace(/0$/, '')}`;
}
